import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { onMessage } from './iframe-bridge'

export interface EmbedParams {
  manufacturerUuid: string | null
  [key: string]: string | null
}

const PARAM_MAP: Record<string, keyof EmbedParams> = {
  manufacturer_uuid: 'manufacturerUuid',
  customer_id: 'manufacturerUuid',
}

function parseUrlParams(): EmbedParams {
  const url = new URL(window.location.href)
  const params: EmbedParams = { manufacturerUuid: null }

  for (const [queryKey, field] of Object.entries(PARAM_MAP)) {
    const val = url.searchParams.get(queryKey)
    if (val) params[field] = val
  }

  return params
}

const EmbedParamsContext = createContext<EmbedParams>({ manufacturerUuid: null })

/**
 * Reads embed parameters from two sources:
 *
 * 1. **URL query string** (available immediately):
 *    `<iframe src="https://app.example.com?manufacturer_uuid=abc123">`
 *
 * 2. **postMessage `parent:config`** (dynamic updates at runtime):
 *    `frame.contentWindow.postMessage({ type: 'parent:config', payload: { manufacturer_uuid: 'abc123' } }, '*')`
 *
 * URL params are read once on mount. postMessage can update/override at any time.
 */
export function EmbedParamsProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useState<EmbedParams>(parseUrlParams)

  useEffect(() => {
    const unsub = onMessage('parent:config', (payload) => {
      const data = payload as Record<string, string> | undefined
      if (!data) return

      setParams((prev) => {
        const next = { ...prev }
        for (const [queryKey, field] of Object.entries(PARAM_MAP)) {
          if (queryKey in data) next[field] = data[queryKey]
        }
        return next
      })
    })

    return unsub
  }, [])

  return (
    <EmbedParamsContext.Provider value={params}>
      {children}
    </EmbedParamsContext.Provider>
  )
}

export function useEmbedParams() {
  return useContext(EmbedParamsContext)
}
